import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import history from "../../history";
import { onLogin, onLogout } from "../../State/actions/user";
import { showLocalizedToaster } from "../../State/actions/toaster";
import Text from "../../components/Text";
import Messages from "../../Shared/Messages";
import I18nProvider from "../../services/I18nProvider";
import { getPublicChannelLanding } from "./publicChannelLink";
import { takeChannelSignIn } from "./channelSignInHandover";
import "./ChannelSignIn.scss";

// Long enough to still be on screen once the login page has rendered.
const ERROR_TOASTER_SECONDS = 7;

const PHASE_STARTING = "starting";
const PHASE_PENDING = "pending";

// /a/signin — where the public URL channel header popover hands its credentials
// over. The visitor is signed in as the channel's shared anonymous account, so
// this ends that session and signs them in as themselves through the ordinary
// LOG_IN saga, then hands over to the app's own role-based landing.
//
// It is a route of its own, outside the patient app, because ending the channel
// session flips state.user.loggedIn to false: inside /app the route guard would
// redirect mid-request and unmount the form that started the sign-in. Only a
// status screen is rendered here.
const ChannelSignIn = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  // Read once, on the first render: the hand-over is one-shot, so keeping the
  // credentials in state survives a re-render that would otherwise find the
  // module empty.
  const [credentials] = useState(takeChannelSignIn);
  // PHASE_PENDING is set through state, not a ref, on purpose: it re-renders, so
  // the outcome effect below reads the user slice as it is *after* the login was
  // dispatched, rather than the mount-render snapshot.
  const [phase, setPhase] = useState(PHASE_STARTING);
  // One-shot hand-off: never two redirects, never two toasters.
  const settled = useRef(false);

  const settle = (path, failureMessage) => {
    if (settled.current) {
      return;
    }
    settled.current = true;
    if (failureMessage) {
      dispatch(
        showLocalizedToaster(
          { content: failureMessage },
          "error",
          ERROR_TOASTER_SECONDS
        )
      );
    }
    history.replace(path);
  };

  // Kick off exactly once on mount.
  useEffect(() => {
    if (!credentials) {
      // Nothing was handed over — a reload of this URL, or someone typing it.
      settle("/login");
      return;
    }
    if (user.loggedIn) {
      // The two resets the profile menu's Logout does, so the sidebar and the
      // locale start clean for whoever signs in next.
      document.body.classList.remove("sidebar-minimized");
      I18nProvider.resetIntlProvider();
      // The channel account is still active. End it first, so the login saga —
      // which waits for a LOG_OUT between sessions — accepts what comes next.
      // skipAggressiveClear: the reducers reset on LOG_OUT anyway, and the
      // storage purge would race the new session's persistence.
      dispatch(onLogout(undefined, true));
    }
    dispatch(onLogin(credentials.username, credentials.password));
    setPhase(PHASE_PENDING);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Follow the saga's outcome through the user slice.
  useEffect(() => {
    if (phase !== PHASE_PENDING || user.loading) {
      return;
    }
    if (user.loggedIn) {
      settle(getPublicChannelLanding(user.role));
    } else if (user.mfaRequired) {
      // The second factor is asked for on the login page, which renders the OTP
      // step from the same mfaRequired state and carries the sign-in to its end.
      settle("/login");
    } else if (user.loginError) {
      // The login page clears loginError as it mounts, so the reason has to
      // travel as a toaster or the visitor lands on a blank form.
      settle("/login", Messages.error_logging_in);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase,
    user.loggedIn,
    user.loading,
    user.loginError,
    user.mfaRequired,
    user.role,
  ]);

  return (
    <main id="main-layout" className="channel-signin">
      <i
        className="fa fa-spinner fa-spin channel-signin__icon"
        aria-hidden="true"
      />
      <h1 className="channel-signin__title">
        <Text content={Messages.entra_signing_in} />
      </h1>
      <p className="channel-signin__text">
        <Text content={Messages.entra_verifying_credentials} />
      </p>
    </main>
  );
};

export default ChannelSignIn;
